'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { unstable_ViewTransition as ViewTransition } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { Project } from '@/types/project';

const FALLBACK_BLUR =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface ProjectCardProps extends Project {
  blurDataURL?: string;
}

export function ProjectCard({
  id,
  title,
  description,
  image,
  blurDataURL,
  technologies,
  category,
  featured = false,
}: ProjectCardProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const finalImage = image || '/assets/placeholder.svg';
  const altText = title?.trim() ? title : 'Project screenshot';
  const effectiveBlurData = blurDataURL || FALLBACK_BLUR;

  return (
    <Link href={`/projects/${id}`} key={id}>
      <ViewTransition
        name={`project-${id}`}
        enter="via-blur"
        exit="duration-100"
        key={id}
      >
        <Card
          className={cn(
            'group relative flex flex-col overflow-hidden transition-all duration-300 focus-within:-translate-y-1 focus-within:shadow-lg hover:-translate-y-1 hover:shadow-lg',
            featured && 'border-primary/20 shadow-primary/10 shadow-lg'
          )}
        >
          {/* Image Section */}

          <div className="bg-muted relative aspect-video overflow-hidden">
            <Image
              src={finalImage}
              alt={altText}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className="object-cover transition-transform duration-300 group-hover:scale-105 hover:cursor-pointer"
              quality={80}
              loading="lazy"
              placeholder="blur"
              blurDataURL={effectiveBlurData}
            />
          </div>

          {/* Featured Badge */}
          <div className="absolute top-4 right-4">
            {featured && (
              <Badge
                variant="default"
                className="min-w-[60px] cursor-pointer rounded-full px-3 py-1 text-xs backdrop-blur-sm sm:min-w-[80px] sm:text-sm"
                onClick={() => {
                  const params = new URLSearchParams(searchParams.toString());
                  params.set('featured', 'true');
                  router.push(`?${params.toString()}#projects`);
                }}
              >
                Featured
              </Badge>
            )}
          </div>

          {/* Category Badge */}
          <div className="absolute top-4 left-4">
            <Badge
              variant="secondary"
              className="min-w-[60px] cursor-pointer rounded-full px-3 py-1 text-xs backdrop-blur-sm sm:min-w-[80px] sm:text-sm"
              onClick={() => {
                const params = new URLSearchParams(searchParams.toString());
                params.set('tech', category);
                router.push(`?${params.toString()}#projects`);
              }}
            >
              {category}
            </Badge>
          </div>

          {/* Content */}
          <CardContent className="flex flex-1 flex-col p-6">
            <CardHeader className="mb-4">
              <CardTitle className="text-xl font-semibold">{title}</CardTitle>

              <CardDescription className="text-muted-foreground line-clamp-3 text-sm">
                {description}
              </CardDescription>
            </CardHeader>

            {/* Tech Stack Buttons */}
            <CardFooter className="mt-auto flex flex-wrap gap-2">
              {technologies.slice(0, 3).map((tech) => (
                <Button
                  key={tech}
                  size="sm"
                  variant="outline"
                  className="hover:cursor-pointer"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('tech', tech);
                    window.history.replaceState(null, '', url.toString());
                  }}
                >
                  {tech}
                </Button>
              ))}
              {technologies.length > 3 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-primary/10 text-xs hover:cursor-pointer"
                >
                  +{technologies.length - 3}
                </Button>
              )}
            </CardFooter>
          </CardContent>
        </Card>
      </ViewTransition>
    </Link>
  );
}
